// src/components/EditProfileModal/index.tsx
import { FC, useEffect, useState, useRef } from 'react';
import { Modal, Form, Input, Select, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import useThemeStore from '@/store/useThemeStore';
import SvgIcon from '@/components/SvgIcon';
import styles from './index.module.scss';

interface ProfileFormValues {
  fullName: string;
  email: string;
  phone: string;
  position: string;
  location: string;
  experience: string;
}

interface EditProfileModalProps {
  open: boolean;
  onCancel: () => void;
  onOk: (values: ProfileFormValues) => void;
  initialValues?: Partial<ProfileFormValues>;
  loading?: boolean;
}

const EditProfileModal: FC<EditProfileModalProps> = ({ open, onCancel, onOk, initialValues = {}, loading = false }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { isDarkMode } = useThemeStore();
  const [isMobile, setIsMobile] = useState(false);
  const wasOpenRef = useRef(false);

  // 根据主题选择 logo
  const mobileLogo = isDarkMode ? '/logo/THEPATCH-D.png' : '/logo/THEPATCH-L.png';
  const mobileLogoSrcSet = isDarkMode ? '/logo/THEPATCH-D.png 1x, /logo/THEPATCH-D@2x.png 2x' : '/logo/THEPATCH-L.png 1x, /logo/THEPATCH-L@2x.png 2x';

  // 检测移动端
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Experience options with i18n support
  const experienceOptions = [
    { label: t('profile.experienceOptions.student'), value: 'student' },
    { label: t('profile.experienceOptions.0-2-years'), value: '0-2-years' },
    { label: t('profile.experienceOptions.3-5-years'), value: '3-5-years' },
    { label: t('profile.experienceOptions.5-plus-years'), value: '5-plus-years' },
  ];

  // 当模态框打开时，设置表单字段的值
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      form.setFieldsValue(initialValues);
    }
    wasOpenRef.current = open;
  }, [open, initialValues, form]);

  const handleOk = () => {
    form.validateFields().then((values) => {
      onOk(values);
    });
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const closeIcon = (
    <svg className={styles.closeIcon} aria-hidden="true">
      <use xlinkHref="#icon-x-black" />
    </svg>
  );

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      title={!isMobile ? t('profile.editProfileModal.title') : undefined}
      width={isMobile ? '100%' : 688}
      className={styles.editProfileModal}
      wrapClassName={isMobile ? styles.editProfileModalWrap : undefined}
      closeIcon={isMobile ? null : closeIcon}
      centered={!isMobile}
      styles={isMobile ? ({ content: { borderRadius: 0, padding: 0 }, body: { padding: 0 } } as never) : undefined}
      footer={
        <div className={styles.modalFooter}>
          <Button className="btn-secondary" size="large" onClick={handleCancel}>
            {t('profile.editProfileModal.cancel')}
          </Button>
          <Button type="primary" size="large" onClick={handleOk} loading={loading}>
            {t('profile.editProfileModal.save')}
          </Button>
        </div>
      }
    >
      {/* 移动端自定义顶部区域 */}
      {isMobile && (
        <div className={styles.mobileHeader}>
          <img src={mobileLogo} srcSet={mobileLogoSrcSet} alt="The Patch" className={styles.mobileLogo} />
          <button className={styles.mobileCloseBtn} onClick={handleCancel} type="button" aria-label="Close">
            <SvgIcon name="x-black" size={24} />
          </button>
        </div>
      )}
      <div className={styles.modalContent}>
        {isMobile && <h2 className={styles.mobileTitle}>{t('profile.editProfileModal.title')}</h2>}
        <Form form={form} layout="vertical" initialValues={initialValues} className={styles.form} requiredMark={false}>
          <div className={styles.formGrid}>
            {/* Full Name */}
            <Form.Item
              label={t('profile.editProfileModal.fullName')}
              name="fullName"
              rules={[{ required: true, message: t('profile.editProfileModal.validation.fullNameRequired') }]}
              className={styles.formItem}
            >
              <Input placeholder={t('profile.editProfileModal.fullNamePlaceholder')} className={styles.input} />
            </Form.Item>

            {/* Email */}
            <Form.Item
              label={t('profile.editProfileModal.email')}
              name="email"
              rules={[
                { required: true, message: t('profile.editProfileModal.validation.emailRequired') },
                { type: 'email', message: t('profile.editProfileModal.validation.emailInvalid') },
              ]}
              className={styles.formItem}
            >
              <Input className={styles.input} disabled />
            </Form.Item>

            {/* Phone */}
            <Form.Item label={t('profile.editProfileModal.phone')} name="phone" className={styles.formItem}>
              <Input placeholder={t('profile.editProfileModal.phonePlaceholder')} className={styles.input} />
            </Form.Item>

            {/* Current Position */}
            <Form.Item label={t('profile.editProfileModal.currentPosition')} name="position" className={styles.formItem}>
              <Input placeholder={t('profile.editProfileModal.positionPlaceholder')} className={styles.input} />
            </Form.Item>

            {/* Location */}
            <Form.Item label={t('profile.editProfileModal.location')} name="location" className={styles.formItem}>
              <Input placeholder={t('profile.editProfileModal.locationPlaceholder')} className={styles.input} />
            </Form.Item>

            {/* Experience */}
            <Form.Item label={t('profile.editProfileModal.experience')} name="experience" className={styles.formItem}>
              <Select placeholder={t('profile.editProfileModal.selectExperience')} options={experienceOptions} className={styles.select} />
            </Form.Item>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default EditProfileModal;
